import React, { useState } from "react"
import PropTypes from "prop-types"
import { Button, Flex, Input, Modal } from "antd"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { routes } from "router/routes"
import { useDevice } from "hooks/useDevice"
import CustomDrawer from "ui/Drawer"
import { UnsavedChangesModal } from "ui/UnsavedChangesModal"
import { Text } from "ui/Typography"
import BlackoutTargetSelector from "../../components/BlackoutTargetSelector"
import AppliesOnRows from "./AppliesOnRows"
import AffectsPanel from "./AffectsPanel"
import { useBlackoutForm } from "./useBlackoutForm"
import { BLACKOUT_SCOPES, SCOPE_COPY, SCOPES_WITH_ALL_OPTION } from "../../constants"
import styles from "./index.module.css"

/**
 * One modal serves all blackout scopes. The "Blackout Settings" and "Affects" cards are
 * identical everywhere; only the target picker inside "Affects" varies, and business
 * blackouts have no picker at all because they apply to everything.
 */
const BlackoutFormModal = ({ isOpen, scope, blackout, onCancel, onSubmit, isSaving }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isMobile } = useDevice()

  const isBusinessScope = scope === BLACKOUT_SCOPES.BUSINESS
  const copy = SCOPE_COPY[scope]

  const {
    reason,
    setReason,
    entries,
    setEntries,
    isRecurringYearly,
    setIsRecurringYearly,
    selectedIds,
    setSelectedIds,
    targetOptions,
    isLoadingTargets,
    impact,
    isLoadingImpact,
    isValid,
    isDirty,
    entryErrors,
    buildPayload
  } = useBlackoutForm({ scope, blackout, isOpen })

  const [isUnsavedPromptOpen, setIsUnsavedPromptOpen] = useState(false)

  /**
   * Every way out of the form routes through here — the Cancel button, the ✕, the mask and
   * Escape all reach antd's `onCancel` — so an edited form can never be discarded silently.
   */
  const requestCancel = () => {
    if (isDirty) {
      setIsUnsavedPromptOpen(true)
      return
    }
    onCancel()
  }

  const discardAndClose = () => {
    setIsUnsavedPromptOpen(false)
    onCancel()
  }

  const title = blackout
    ? t("settings.blackout.edit_title", { defaultValue: "Edit Blackout" })
    : t(copy.titleKey, { defaultValue: copy.titleDefault })

  const content = (
    <div className={styles.content}>
      <section className={styles.card}>
        <Text variant="sub" size="lg" fw="600" className={styles.cardTitle}>
          {t("settings.blackout.settings_section", { defaultValue: "Blackout Settings" })}
        </Text>

        <label className={styles.field}>
          <Text variant="sub" size="md" fw="500" className={styles.fieldLabel}>
            {t("settings.blackout.reason", { defaultValue: "Reason/Note" })}
            <span className={styles.optional}>{t("settings.blackout.optional", { defaultValue: "(Optional)" })}</span>
          </Text>
          <Input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={t("settings.blackout.reason_placeholder", { defaultValue: "e.g. Weather closures" })}
          />
        </label>

        <AppliesOnRows
          entries={entries}
          onChange={setEntries}
          isRecurringYearly={isRecurringYearly}
          onRecurringChange={setIsRecurringYearly}
          entryErrors={entryErrors}
        />
      </section>

      <section className={styles.card}>
        <Text variant="sub" size="lg" fw="600" className={styles.cardTitle}>
          {t("settings.blackout.affects_section", { defaultValue: "Affects" })}
        </Text>

        {!isBusinessScope && (
          <div className={styles.field}>
            <Text variant="sub" size="md" fw="500" className={styles.fieldLabel}>
              {t(copy.targetLabelKey, { defaultValue: copy.targetLabelDefault })}
              <span className={styles.required}>*</span>
            </Text>
            <BlackoutTargetSelector
              options={targetOptions}
              value={selectedIds}
              onChange={setSelectedIds}
              placeholder={t("settings.blackout.select_placeholder", { defaultValue: "Select" })}
              showAllOption={SCOPES_WITH_ALL_OPTION.includes(scope)}
              isLoading={isLoadingTargets}
            />
          </div>
        )}

        <AffectsPanel
          scope={scope}
          impact={impact}
          isLoading={isLoadingImpact}
          hasTargets={selectedIds.length > 0}
          onViewBookings={() => navigate(routes.guideBookingsPath())}
        />
      </section>

      <Flex justify="flex-end" gap="var(--spacing-s)" className={styles.footer}>
        <Button shape="round" onClick={requestCancel} disabled={isSaving}>
          {t("settings.blackout.cancel", { defaultValue: "Cancel" })}
        </Button>
        <Button
          color="primary"
          variant="solid"
          shape="round"
          onClick={() => onSubmit(buildPayload())}
          // Applying mid-preview would commit a blackout whose stated impact is still the old one.
          disabled={!isValid || isSaving || isLoadingImpact}
          loading={isSaving}
        >
          {t("settings.blackout.apply", { defaultValue: "Apply Blackout" })}
        </Button>
      </Flex>
    </div>
  )

  const unsavedPrompt = (
    <UnsavedChangesModal
      open={isUnsavedPromptOpen}
      onDiscard={discardAndClose}
      onKeepEditing={() => setIsUnsavedPromptOpen(false)}
      zIndex={1200}
    />
  )

  if (isMobile) {
    return (
      <>
        <CustomDrawer title={title} open={isOpen} setOpen={requestCancel}>
          {content}
        </CustomDrawer>
        {unsavedPrompt}
      </>
    )
  }

  return (
    <Modal open={isOpen} onCancel={requestCancel} title={title} width={800} centered footer={null} className={styles.modal}>
      {content}
      {unsavedPrompt}
    </Modal>
  )
}

BlackoutFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  scope: PropTypes.oneOf(Object.values(BLACKOUT_SCOPES)).isRequired,
  blackout: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSaving: PropTypes.bool
}

export default BlackoutFormModal
